import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle, Button } from '@parrot-software/design-system';
import { CircleAlert } from '@parrot-software/design-system/icons';
import { logger } from '@parrot-software/observability';
import { cn } from '@parrot-software/utils';
import { OVERDUE_PAYMENT_ORIGIN } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import type { OverdueChargeRow } from '@subscriptions/presentation/utils/overdueChargeRow';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });

interface BlockPendingPaymentAlertProps {
  charge: OverdueChargeRow;
}

/**
 * BlockPendingPaymentAlert - Alert for a charge the subscription has not settled yet.
 *
 * Takes the row the table already lists rather than loose values, so it cannot be mounted for a
 * subscription that owes nothing or with a date that does not read.
 *
 * The action re-enters the payment wizard, the same way the payment method card does.
 *
 * @param {Object} props - Component properties
 * @returns {React.ReactElement} Destructive alert with the pay action
 */
export const BlockPendingPaymentAlert = ({ charge }: BlockPendingPaymentAlertProps) => {
  const { t } = useTranslation('paymentHistory');
  const history = useHistory();
  const areActionsLocked = useAreSubscriptionActionsLocked();

  const goToPayment = () => {
    paymentLog.info('openPendingPaymentWizard', { 'block.uuid': charge.blockUuid });
    history.push(`/subscriptions/blocks/create/${charge.blockUuid}/payment?origin=${OVERDUE_PAYMENT_ORIGIN}`);
  };

  return (
    <Alert
      className={cn(
        'flex items-start gap-3 rounded-lg bg-destructive/10 font-sans',
        '[&>svg]:relative [&>svg]:left-auto [&>svg]:top-auto [&>svg+div]:translate-y-0 [&>svg~*]:pl-0',
      )}
      data-testid="payment-history-pending-payment-alert"
      variant="destructive"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex flex-col items-start gap-2">
        <AlertTitle className="mb-0 text-sm font-bold">
          {t('pendingPaymentNote.title', { date: charge.paymentDate })}
        </AlertTitle>
        <AlertDescription className="text-sm leading-relaxed">{t('pendingPaymentNote.message')}</AlertDescription>
        <Button className="h-8" disabled={areActionsLocked} onClick={goToPayment} size="sm">
          {t('pendingPaymentNote.action')}
        </Button>
      </div>
    </Alert>
  );
};
